import { Icon } from "@/assets/icons";
import assignmentsStyles from "@features/assignments/styles/assignment.module.scss";
import { IListTimeAssignmentsResponse } from "@/api/timeAssignments/types";
import CancelIco from "@features/assignments/assets/cancel.svg?react";
import type { IconName } from "@/assets/icons/Icons";
import {
  generateHoursTooltip,
  getHoursElementStyles,
} from "@/features/assignments/utils/hoursColorSystem";

const pastelColors = [
  "#F2C94C",
  "#F2C966",
  "#F2C981",
  "#F2C99C",
  "#F2C9B7",
  "#F2C9D1",
  "#F2C9E5",
  "#F2C9F2",
  "#E1C9F2",
  "#D3C9F2",
  "#C9D6F2",
  "#C9E1F2",
  "#C9ECF2",
  "#C9F2EC",
  "#C9F2E1",
  "#C9F2D6",
  "#C9F2C9",
];

export interface IAssignmentCalendarBodyProps {
  item: IListTimeAssignmentsResponse;
  active: boolean;
  arrow: string;
  handleCollapseItem: () => void;
  changeDisabled: () => void;
  openAddTaskModal: () => void;
  closeAddTaskModal: () => void;
  editTrue: boolean;
  isRealTimeDisabled: boolean;
  openDescription: boolean;
  addTaskIcon: boolean;
  isAdmin: boolean;
  week: string[]; // Agregamos week para calcular totales
}

function getRandomPastelColor() {
  const randomIndex = Math.floor(Math.random() * pastelColors.length + 0.5);
  return pastelColors[randomIndex];
}

function renderUserImage(
  item: IListTimeAssignmentsResponse,
  style: React.CSSProperties
) {
  console.log("🚀 ~ renderUserImage ~ item:", item);
  return (
    <div
      className={assignmentsStyles["assigncalenbody__name-item"]}
      style={style}
    ></div>
  );
}

const renderIconButton = (
  iconName: IconName,
  color: string,
  onClick: () => void,
  extraClass = ""
) => {
  return (
    <button
      className={`${assignmentsStyles["assigncalenbody__space"]} ${assignmentsStyles["assigncalenbody__space__change"]} ${extraClass}`}
      onClick={onClick}
    >
      <Icon name={iconName} color={color} />
    </button>
  );
};

function getArrowFilterStyle(active: boolean) {
  return {
    filter: `sepia(${active ? 1 : 0}) saturate(${active ? 3 : 0}) hue-rotate(${
      active ? 130 : 0
    }deg)`,
  };
}

function renderAddTaskButton(
  addTaskIcon: boolean,
  closeAddTaskModal: () => void,
  openAddTaskModal: () => void
) {
  return addTaskIcon ? (
    <button onClick={closeAddTaskModal}>
      <CancelIco height="25" />
    </button>
  ) : (
    <button onClick={openAddTaskModal}>
      <Icon name="more" color={"#3D3D3D"} />
    </button>
  );
}

function renderEditButton(
  editTrue: boolean,
  isRealTimeDisabled: boolean,
  changeDisabled: () => void
) {
  let color: string;

  if (!isRealTimeDisabled) {
    color = "#4BDED5";
  } else {
    color = editTrue ? "#3D3D3D" : "#BDBDBD";
  }
  return renderIconButton("edit", color, changeDisabled);
}

// Función para calcular totales por día para una persona
function calculatePersonTotals(
  item: IListTimeAssignmentsResponse,
  week: string[]
) {
  const dailyTotals = week.map((date) => {
    let totalEstimated = 0;
    let totalReal = 0;

    // Iterar por todas las asignaciones de la persona
    item.assignments?.forEach((assignment) => {
      // Iterar por todos los schedules de cada asignación
      assignment.schedules?.forEach((schedule) => {
        const scheduleDate = schedule.scheduleDate.substring(0, 10);
        if (scheduleDate === date) {
          totalEstimated += Number(schedule.estimated_time) || 0;
          totalReal += Number(schedule.real_time) || 0;
        }
      });
    });

    // Siempre retornar un objeto, incluso si no hay datos (ceros)
    return {
      date,
      totalEstimated: totalEstimated || 0,
      totalReal: totalReal || 0,
    };
  });

  return dailyTotals;
}

// Las funciones de color ahora se importan desde hoursColorSystem.ts
// Esto mejora la reutilización y mantenibilidad del código

// Función para renderizar los totales diarios
function renderDailyTotals(
  dailyTotals: Array<{
    date: string;
    totalEstimated: number;
    totalReal: number;
  }>
) {
  return dailyTotals.map((day) => {
    const styles = getHoursElementStyles(day.totalEstimated, false);
    const tooltip = generateHoursTooltip(day.totalEstimated, false);

    return (
      <div
        key={day.date}
        className={assignmentsStyles["assigncalenbody__day-totals"]}
      >
        <div
          className={assignmentsStyles["assigncalenbody__day-total-est"]}
          style={styles}
          title={tooltip}
        >
          {day.totalEstimated > 0 ? day.totalEstimated.toFixed(1) : "0"}
        </div>
        <div className={assignmentsStyles["assigncalenbody__day-total-real"]}>
          {day.totalReal > 0 ? day.totalReal.toFixed(1) : "0"}
        </div>
      </div>
    );
  });
}

export const AssignmentCalendarBody = ({
  arrow,
  item,
  active,
  handleCollapseItem,
  changeDisabled,
  openAddTaskModal,
  closeAddTaskModal,
  editTrue,
  isRealTimeDisabled,
  openDescription,
  addTaskIcon,
  isAdmin,
  week,
}: IAssignmentCalendarBodyProps) => {
  const StyleImage1 = { backgroundColor: getRandomPastelColor() };
  const dailyTotals = calculatePersonTotals(item, week);

  // Determinar la clase CSS basada en el estado
  const containerClassName = openDescription
    ? assignmentsStyles["assigncalenbody__borderoff"]
    : assignmentsStyles["assigncalenbody"];

  return (
    <div
      className={containerClassName}
      data-testid="assignment-calendar-body"
      data-expanded={openDescription}
    >
      {/* Primera columna: Información del usuario */}
      <div className={assignmentsStyles["assigncalenbody__col"]}>
        <div className={assignmentsStyles["assigncalenbody__members"]}>
          {renderUserImage(item, StyleImage1)}
          <div className={assignmentsStyles["assigncalenbody__name"]}>
            {item.fullName}
          </div>
        </div>
        <div className={assignmentsStyles["assigncalenbody__space"]}></div>
        <div
          id="add task icon container"
          className={`${assignmentsStyles["assigncalenbody__space"]}`}
        >
          {isAdmin &&
            renderAddTaskButton(
              addTaskIcon,
              closeAddTaskModal,
              openAddTaskModal
            )}
        </div>
        <button
          id="arrow icon container"
          className={`${assignmentsStyles["assigncalenbody__space"]}`}
          onClick={handleCollapseItem}
        >
          <img
            src={arrow}
            alt="arrow icon"
            style={getArrowFilterStyle(active)}
          />
        </button>
        {renderEditButton(editTrue, isRealTimeDisabled, changeDisabled)}
        <div className={assignmentsStyles["assigncalenbody__arrow"]}>
          <span className={assignmentsStyles["assigncalenbody__arrow-label"]}>
            EST
          </span>
          <span
            className={`${assignmentsStyles["assigncalenbody__arrow-label"]} ${
              active ? assignmentsStyles["active"] : ""
            }`}
          >
            REAL
          </span>
        </div>
      </div>

      {/* Segunda columna: Totales diarios */}
      <div className={assignmentsStyles["assigncalenbody__col"]}>
        {renderDailyTotals(dailyTotals)}
      </div>

      {/* Tercera columna: Totales semanales */}
      <div className={assignmentsStyles["assigncalenbody__col"]}>
        {(() => {
          const weeklyEstimated = dailyTotals.reduce(
            (sum, day) => sum + day.totalEstimated,
            0
          );
          const weeklyStyles = getHoursElementStyles(weeklyEstimated, true);
          const weeklyTooltip = generateHoursTooltip(weeklyEstimated, true);

          return (
            <div
              className={`${assignmentsStyles["assigncalenbody__sum"]} ${
                active ? assignmentsStyles["active"] : ""
              }`}
              style={weeklyStyles}
              title={weeklyTooltip}
            >
              {weeklyEstimated.toFixed(1)}
            </div>
          );
        })()}
        <div className={assignmentsStyles["assigncalenbody__sum-total"]}>
          {dailyTotals.reduce((sum, day) => sum + day.totalReal, 0).toFixed(1)}
        </div>
      </div>
    </div>
  );
};
